import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

/**
 * SanctuaryModerationDialog — surfaces backend "Sanctuary Moderation"
 * blocks in a distinct red modal. Two variants are handled:
 *
 *   • 'blocked'     — the content was judged inappropriate for a mainstream
 *                     Christian church sanctuary. Shows the AI's specific
 *                     reason and an "Edit and try again" CTA.
 *   • 'unavailable' — the moderation service itself errored (network,
 *                     timeout, quota, malformed reply). We failed CLOSED to
 *                     protect the app; user is asked to retry.
 *
 * Props:
 *   open    — visibility
 *   variant — 'blocked' | 'unavailable'
 *   reason  — human-readable reason from the backend
 *   source  — the field/kind the moderator objected to (e.g. 'title',
 *             'description', 'comment', 'handle', 'profile_picture')
 *   onClose — dismiss callback (also serves as "Edit and try again")
 */
const SanctuaryModerationDialog = ({ open, variant = 'blocked', reason, source, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isBlocked = variant === 'blocked';
  const title = isBlocked ? 'This isn\u2019t appropriate in a place of prayer' : 'Moderation is temporarily unavailable';
  const body = isBlocked
    ? (
      <>
        <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
          Pray Now Together moderates every submission against a simple rule:
          <em className="mx-1 italic">
            would this be appropriate in a place of prayer?
          </em>
        </p>
        {source && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
            Flagged in: <span className="font-medium text-zinc-700 dark:text-zinc-200">{source.replace(/_/g, ' ')}</span>
          </p>
        )}
        {reason && (
          <div
            className="mt-3 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200 text-sm px-3 py-2"
            data-testid="sanctuary-reason"
          >
            {reason}
          </div>
        )}
      </>
    )
    : (
      <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
        {reason || 'We couldn\u2019t reach the moderation service. Please wait a moment and try again.'}
      </p>
    );

  return createPortal((
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 isolate"
      onClick={onClose}
      data-testid="sanctuary-moderation-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-zinc-900 border-2 border-red-500 dark:border-red-600 shadow-2xl overflow-hidden"
        data-testid="sanctuary-moderation-dialog"
        role="alertdialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
          <div className="flex items-center gap-2 text-lg font-semibold text-red-700 dark:text-red-300">
            <AlertTriangle size={20} className="text-red-500" />
            <span data-testid="sanctuary-dialog-title">{title}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="sanctuary-dialog-close"
            aria-label="Close"
            className="p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 pt-1 pb-5">
          {body}
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="sanctuary-dialog-cta"
              className={
                'h-10 px-4 rounded-xl text-sm font-semibold text-white ' +
                (isBlocked ? 'bg-red-600 hover:bg-red-700' : 'bg-zinc-900 dark:bg-white dark:text-zinc-900 hover:bg-zinc-800')
              }
            >
              {isBlocked ? 'Edit and try again' : 'Got it'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
};

export default SanctuaryModerationDialog;

/**
 * Helper: given any axios error, return `{ variant, reason, source } | null`
 * if the backend was a Sanctuary Moderation response. Otherwise returns null
 * so the caller can fall back to its normal error handling.
 */
export function parseSanctuaryError(err) {
  const d = err?.response?.data?.detail;
  if (!d || typeof d !== 'object') return null;
  if (d.error === 'sanctuary_moderation_failed') {
    return { variant: 'blocked', reason: d.reason, source: d.source };
  }
  if (d.error === 'sanctuary_moderation_unavailable') {
    return { variant: 'unavailable', reason: d.reason, source: null };
  }
  return null;
}
